import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign,
  Package,
  ShoppingCart,
  Target,
  Wallet,
  TrendingUp,
  Award,
  Flame,
  Camera,
  User,
  AlertTriangle,
  Smartphone,
  TrendingDown,
  Link2,
  Copy,
  Check,
  ExternalLink,
  QrCode,
  CheckCircle,
  Circle,
  ChevronRight,
  CreditCard,
  FileText,
  Share2,
  Sparkles,
} from "lucide-react";

export default function VendedorDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [inventario, setInventario] = useState([]);
  const [comisionData, setComisionData] = useState(null);
  const [nominaData, setNominaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fotoDialogOpen, setFotoDialogOpen] = useState(false);
  const [fotoUrl, setFotoUrl] = useState("");
  const [catalogLinkCopied, setCatalogLinkCopied] = useState(false);

  const isDigital = user?.tipo_vendedor === "digital";
  
  // Catalog URL
  const catalogUrl = user?.catalog_code 
    ? `${window.location.origin}/catalogo/${user.catalog_code}`
    : null;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ventasRes, inventarioRes, comisionRes, nominaRes] = await Promise.all([
        api.get("/ventas/hoy"),
        api.get("/inventario"),
        api.get("/comisiones/calcular"),
        api.get("/nomina/resumen"),
      ]);
      setData(ventasRes.data);
      setInventario(inventarioRes.data);
      setComisionData(comisionRes.data);
      setNominaData(nominaRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFotoUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/usuarios/${user.id}/foto`, { foto_url: fotoUrl });
      toast.success("Foto actualizada");
      setFotoDialogOpen(false);
      window.location.reload();
    } catch (error) {
      toast.error("Error al actualizar foto");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const fecha = new Date().toLocaleDateString("es-CO", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const meta = user?.meta_diaria || 150000;
  const progreso = data ? Math.min(Math.round((data.total / meta) * 100), 100) : 0;
  const comision = comisionData?.hoy?.total_comision || comisionData?.hoy?.ingreso_estimado || 0;
  const totalInventario = inventario.reduce((sum, i) => sum + (i.cantidad_disponible || 0), 0);
  const faltaParaMeta = Math.max(0, meta - (data?.total || 0));

  // Cálculo de dinero dejado de ganar
  const metaDiariaComision = nominaData?.semana?.meta_diaria_comision || 15000;
  const comisionMaximaPotencial = nominaData?.semana?.comision_maxima_potencial || 0;
  const totalGanado = nominaData?.semana?.total_ganado || 0;
  const dineroDeadoDeGanar = Math.max(0, comisionMaximaPotencial - totalGanado);
  const porcentajeComision = comisionMaximaPotencial > 0 
    ? ((totalGanado / comisionMaximaPotencial) * 100).toFixed(1) 
    : 0;

  // Mensajes motivacionales según progreso
  const getMensajeMotivacional = () => {
    if (progreso >= 100) return { emoji: "🏆", texto: "¡CAMPEÓN! ¡Superaste tu meta!" };
    if (progreso >= 75) return { emoji: "🔥", texto: "¡Vas en llamas! ¡Ya casi llegas!" };
    if (progreso >= 50) return { emoji: "💪", texto: "¡Buen ritmo! ¡Sigue así!" };
    if (progreso >= 25) return { emoji: "🚀", texto: "¡Arrancaste bien! ¡A por más!" };
    return { emoji: "⭐", texto: "¡El día apenas comienza!" };
  };

  const mensaje = getMensajeMotivacional();
  
  const copyCatalogLink = () => {
    if (catalogUrl) {
      navigator.clipboard.writeText(catalogUrl);
      setCatalogLinkCopied(true);
      toast.success("Link del catálogo copiado");
      setTimeout(() => setCatalogLinkCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedor-dashboard">
      {/* Header con foto de perfil */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setFotoDialogOpen(true)}
            className="relative group"
          >
            {user?.foto_url ? (
              <img
                src={user.foto_url}
                alt={user.nombre}
                className="w-16 h-16 rounded-full object-cover border-4 border-torta-brown"
              />
            ) : (
              <div className="w-16 h-16 bg-torta-cream rounded-full flex items-center justify-center border-4 border-torta-brown">
                <User className="w-8 h-8 text-torta-brown" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-5 h-5 text-white" />
            </div>
          </button>
          <div>
            <h1 className="font-display text-2xl text-torta-dark">
              ¡Hola, {user?.nombre}!
            </h1>
            <p className="text-torta-muted text-sm capitalize">{fecha}</p>
            <div className="flex items-center gap-2 mt-1">
              {user?.zona && (
                <span className="text-torta-brown text-xs font-medium">Zona: {user.zona}</span>
              )}
              {isDigital ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                  <Smartphone className="w-3 h-3" />
                  Digital
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
                  <ShoppingCart className="w-3 h-3" />
                  Carrito
                </span>
              )}
            </div>
          </div>
        </div>
        <img
          src="/logo-torta-cuajada.png"
          alt="Logo"
          className="w-12 h-12 rounded-full object-cover"
        />
      </div>

      {/* Checklist de Activación - Solo si no está completo el onboarding */}
      {!user?.onboarding_completo && (
        <div className="bg-gradient-to-br from-[#FEF3E2] to-[#FFF9F0] rounded-2xl p-5 border border-[#E5D4B3] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C8902A]/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#C8902A]" />
              </div>
              <div>
                <h3 className="font-display text-lg text-torta-dark">Completa tu perfil</h3>
                <p className="text-xs text-torta-muted">Para empezar a vender y recibir pagos</p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-[#C8902A]">
                {[
                  user?.foto_url,
                  user?.banco && user?.numero_cuenta,
                  user?.contrato_aceptado,
                  catalogUrl
                ].filter(Boolean).length}/4
              </span>
              <p className="text-xs text-torta-muted">pasos</p>
            </div>
          </div>
          
          <div className="space-y-2">
            {/* Paso 1: Foto de perfil */}
            <div 
              onClick={() => !user?.foto_url && setFotoDialogOpen(true)}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                user?.foto_url 
                  ? "bg-green-50 cursor-default" 
                  : "bg-white hover:bg-[#C8902A]/5 cursor-pointer"
              }`}
            >
              {user?.foto_url ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-[#C8902A] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${user?.foto_url ? "text-green-700" : "text-torta-dark"}`}>
                  Foto de perfil
                </p>
                <p className="text-xs text-torta-muted truncate">
                  {user?.foto_url ? "¡Listo!" : "Agrega tu foto para generar confianza"}
                </p>
              </div>
              {!user?.foto_url && <ChevronRight className="w-4 h-4 text-torta-muted" />}
            </div>

            {/* Paso 2: Datos bancarios */}
            <Link 
              to="/vendedor/perfil"
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                user?.banco && user?.numero_cuenta
                  ? "bg-green-50" 
                  : "bg-white hover:bg-[#C8902A]/5"
              }`}
            >
              {user?.banco && user?.numero_cuenta ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-[#C8902A] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${user?.banco && user?.numero_cuenta ? "text-green-700" : "text-torta-dark"}`}>
                  Datos bancarios
                </p>
                <p className="text-xs text-torta-muted truncate">
                  {user?.banco && user?.numero_cuenta ? `${user.banco} - ****${user.numero_cuenta?.slice(-4)}` : "Para recibir tus comisiones"}
                </p>
              </div>
              {!(user?.banco && user?.numero_cuenta) && <ChevronRight className="w-4 h-4 text-torta-muted" />}
            </Link>

            {/* Paso 3: Contrato */}
            <Link 
              to="/vendedor/perfil"
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                user?.contrato_aceptado
                  ? "bg-green-50" 
                  : "bg-white hover:bg-[#C8902A]/5"
              }`}
            >
              {user?.contrato_aceptado ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-[#C8902A] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${user?.contrato_aceptado ? "text-green-700" : "text-torta-dark"}`}>
                  Términos y contrato
                </p>
                <p className="text-xs text-torta-muted truncate">
                  {user?.contrato_aceptado ? "Aceptado" : "Lee y acepta los términos del servicio"}
                </p>
              </div>
              {!user?.contrato_aceptado && <ChevronRight className="w-4 h-4 text-torta-muted" />}
            </Link>

            {/* Paso 4: Compartir catálogo */}
            <div 
              onClick={catalogUrl ? copyCatalogLink : undefined}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                catalogLinkCopied
                  ? "bg-green-50 cursor-default" 
                  : catalogUrl
                  ? "bg-white hover:bg-[#C8902A]/5 cursor-pointer"
                  : "bg-gray-50 cursor-not-allowed opacity-60"
              }`}
            >
              {catalogLinkCopied ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-[#C8902A] flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${catalogLinkCopied ? "text-green-700" : "text-torta-dark"}`}>
                  Comparte tu catálogo
                </p>
                <p className="text-xs text-torta-muted truncate">
                  {catalogLinkCopied ? "¡Link copiado!" : catalogUrl ? "Copia tu link para empezar a vender" : "Sin catálogo asignado"}
                </p>
              </div>
              {catalogUrl && !catalogLinkCopied && <ChevronRight className="w-4 h-4 text-torta-muted" />}
            </div>
          </div>
        </div>
      )}

      {/* Mensaje Motivacional */}
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-4">
          <span className="text-5xl">{mensaje.emoji}</span>
          <div>
            <p className="font-display text-xl">{mensaje.texto}</p>
            {progreso < 100 && (
              <p className="text-white/90 text-sm mt-1">
                Te faltan {formatCurrency(faltaParaMeta)} para cumplir tu meta
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Indicador de Oportunidad de Comisión */}
      {dineroDeadoDeGanar > 0 && (
        <div className="bg-gradient-to-r from-slate-600 to-slate-500 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-white/80 text-sm">Oportunidad de comisión esta semana</p>
              <p className="font-display text-xl mt-1">{formatCurrency(dineroDeadoDeGanar)}</p>
              <p className="text-white/60 text-xs mt-1">
                Llevas {porcentajeComision}% de tu meta semanal
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Card - Mejorado con gráfico circular */}
      <div className="bg-gradient-to-br from-torta-brown to-[#A0522D] rounded-2xl p-6 text-white overflow-hidden relative">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-8 h-8" />
              <div>
                <p className="text-white/80 text-sm">Meta del día</p>
                <p className="font-display text-2xl">{formatCurrency(meta)}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/80">Vendido</span>
                <span className="font-bold">{formatCurrency(data?.total)}</span>
              </div>
              <div className="h-4 bg-white/20 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${
                    progreso >= 100 ? "bg-green-400" : progreso >= 50 ? "bg-yellow-400" : "bg-white"
                  }`}
                  style={{ width: `${progreso}%` }}
                />
              </div>
            </div>
          </div>

          {/* Círculo de progreso */}
          <div className="relative w-28 h-28 ml-6">
            <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="10"
                fill="none"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke={progreso >= 100 ? "#4ade80" : progreso >= 50 ? "#facc15" : "#fff"}
                strokeWidth="10"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${progreso * 2.51} 251`}
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display text-2xl">{progreso}%</span>
            </div>
          </div>
        </div>

        {progreso >= 100 && (
          <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-3">
            <Award className="w-6 h-6 text-yellow-300" />
            <p className="font-medium">¡Felicidades! ¡Meta superada!</p>
            <Flame className="w-6 h-6 text-orange-300 animate-pulse" />
          </div>
        )}
      </div>

      {/* Stats Grid - Diferenciado por tipo */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-torta-muted text-sm">Ventas Hoy</span>
            <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="font-display text-2xl text-torta-dark">
            {formatCurrency(data?.total)}
          </p>
          <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" />
            {data?.cantidad_ventas || 0} ventas
          </p>
        </div>

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-torta-muted text-sm">Unidades</span>
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="font-display text-2xl text-torta-dark">
            {data?.unidades || 0}
          </p>
          <p className="text-xs text-torta-muted mt-1">vendidas hoy</p>
        </div>

        {/* KPI diferenciado: Inventario para Carrito, Pedidos para Digital */}
        {isDigital ? (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-torta-muted text-sm">Comisión Semanal</span>
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="font-display text-2xl text-torta-dark">
              {formatCurrency(totalGanado)}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {porcentajeComision}% de meta
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-torta-muted text-sm">Mi Inventario</span>
              <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <p className="font-display text-2xl text-torta-dark">
              {totalInventario} und
            </p>
            <p className="text-xs text-torta-muted mt-1">disponible</p>
          </div>
        )}

        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-torta-muted text-sm">Mi Comisión Hoy</span>
            <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="font-display text-2xl text-torta-dark">
            {formatCurrency(comision)}
          </p>
          <p className="text-xs text-torta-muted mt-1">del día</p>
        </div>
      </div>

      {/* Widget Catálogo Digital */}
      {user?.catalog_code && (
        <div className="bg-gradient-to-r from-[#4A2C0A] to-[#6D4C1A] rounded-2xl p-5 text-white overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center">
                <Link2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display text-lg">Tu Catálogo Digital</h3>
                <p className="text-white/70 text-xs">Comparte y vende sin inventario</p>
              </div>
            </div>
            <button
              onClick={() => window.open(catalogUrl, "_blank")}
              className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
          
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-white/60 mb-1">Tu link personalizado:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm text-white/90 truncate bg-white/5 px-2 py-1 rounded">
                {catalogUrl}
              </code>
              <button
                onClick={copyCatalogLink}
                className="flex-shrink-0 w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                {catalogLinkCopied ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          
          <div className="mt-3 flex gap-2">
            <Button
              onClick={copyCatalogLink}
              className="flex-1 bg-white text-[#4A2C0A] hover:bg-white/90 h-10 rounded-xl font-medium"
            >
              <Copy className="w-4 h-4 mr-2" /> Copiar Link
            </Button>
            <Button
              onClick={() => {
                const text = `¡Hola! Te comparto mi catálogo de Tortas de Cuajada Baruj Shefa. Haz tu pedido aquí: ${catalogUrl}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
              }}
              className="flex-1 bg-[#25D366] hover:bg-[#128C7E] text-white h-10 rounded-xl font-medium"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Compartir
            </Button>
          </div>
        </div>
      )}
      {!isDigital && inventario.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-display text-lg text-torta-dark mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-torta-brown" />
            Mi Stock
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {inventario.map((item) => (
              <div
                key={item.id}
                className={`text-center p-3 rounded-xl ${
                  item.cantidad_disponible > 5
                    ? "bg-green-50"
                    : item.cantidad_disponible > 0
                    ? "bg-amber-50"
                    : "bg-red-50"
                }`}
              >
                <p className="text-xs text-torta-muted truncate">{item.producto_nombre}</p>
                <p
                  className={`font-display text-xl ${
                    item.cantidad_disponible > 5
                      ? "text-green-600"
                      : item.cantidad_disponible > 0
                      ? "text-amber-600"
                      : "text-red-600"
                  }`}
                >
                  {item.cantidad_disponible}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumen Semanal - Para Digital */}
      {isDigital && nominaData && (
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-display text-lg text-torta-dark mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Resumen Semanal
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <p className="text-xs text-blue-600">Unidades vendidas</p>
              <p className="font-display text-2xl text-blue-800">
                {nominaData.semana?.unidades_vendidas || 0}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl">
              <p className="text-xs text-green-600">Días transcurridos</p>
              <p className="font-display text-2xl text-green-800">
                {nominaData.semana?.dias_transcurridos || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl col-span-2">
              <p className="text-xs text-purple-600">Meta comisión semanal</p>
              <p className="font-display text-xl text-purple-800">
                {formatCurrency(comisionMaximaPotencial)}
              </p>
              <p className="text-xs text-purple-500 mt-1">
                {formatCurrency(metaDiariaComision)}/día × {nominaData.semana?.dias_transcurridos || 0} días
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions - Diferenciadas por tipo */}
      <div className="grid grid-cols-2 gap-4">
        <Link
          to="/vendedor/venta"
          className="bg-torta-brown text-white rounded-2xl p-6 hover:bg-torta-brown/90 transition-colors"
        >
          <ShoppingCart className="w-8 h-8 mb-3" />
          <p className="font-medium">Nueva Venta</p>
          <p className="text-xs text-white/80">Registrar venta</p>
        </Link>

        {isDigital ? (
          <Link
            to="/vendedor/desempeno"
            className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <TrendingUp className="w-8 h-8 text-blue-600 mb-3" />
            <p className="font-medium text-torta-dark">Mis Ganancias</p>
            <p className="text-xs text-torta-muted">Ver detalle</p>
          </Link>
        ) : (
          <Link
            to="/vendedor/cierre"
            className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <Wallet className="w-8 h-8 text-torta-brown mb-3" />
            <p className="font-medium text-torta-dark">Cierre de Caja</p>
            <p className="text-xs text-torta-muted">Hacer cierre</p>
          </Link>
        )}
      </div>

      {/* Foto Dialog */}
      <Dialog open={fotoDialogOpen} onOpenChange={setFotoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Actualizar Foto de Perfil</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFotoUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">URL de la imagen</label>
              <Input
                type="url"
                value={fotoUrl}
                onChange={(e) => setFotoUrl(e.target.value)}
                placeholder="https://ejemplo.com/mi-foto.jpg"
                required
              />
              <p className="text-xs text-torta-muted">
                Puedes subir tu foto a un servicio como Imgur y pegar el enlace aquí
              </p>
            </div>
            {fotoUrl && (
              <div className="flex justify-center">
                <img
                  src={fotoUrl}
                  alt="Preview"
                  className="w-24 h-24 rounded-full object-cover border-4 border-torta-brown"
                  onError={(e) => e.target.style.display = "none"}
                />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFotoDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-torta-brown hover:bg-torta-brown/90">
                Guardar Foto
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
